import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { RadioGroup } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { captureException } from '@sentry/nextjs';
import { Elements } from '@stripe/react-stripe-js';
import { SetupIntent } from '@stripe/stripe-js';
import { CardBrandsDisplayName } from 'constants/payments';
import { GetUserCreditCardsQuery } from 'types/generated/client';
import { toFormattedPhone } from 'utils/shared/phone/toFormatedPhone';
import { toSafePhone } from 'utils/shared/phone/toSafePhone';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useModal } from 'hooks/useModal';
import { useViewer } from 'hooks/useViewer';
import Plus from 'svg/Plus';
import {
  AddCardForm,
  LoadingSkeletonCollection,
  NewCardCheckoutForm,
  useCoachPayment,
} from 'screens/CoachDetails/LessonScheduler/PaymentForm';
import { ManagePaymentCardsProps } from 'screens/CoachDetails/PremiumPlayerSide/props';
import Button from 'components/Button';
import Pill from 'components/Pill';
import Modal from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';

const ManagePaymentCards = forwardRef(
  (
    { isLoading, handleSubmitPayment, hideSubmitBtn = false }: ManagePaymentCardsProps,
    parentConrolRef,
  ) => {
    const {
      isOpen: isAddCardModalOpen,
      openModal: openAddCardModal,
      closeModal: closeAddCardModal,
    } = useModal();

    const [selectedCardIndex, setSelectedCardIndex] = useState(0);

    const [isStripeReady, setIsStripeReady] = useState<boolean>(false);

    const formRef = useRef<HTMLFormElement>(null);

    const { user } = useGetCurrentUser();

    const {
      stripeKeys,
      stripe: stripeInternal,
      creditCards: savedCreditCards,
      isLoadingCreditCards,
      fetchSetupIntent,
      isStripeKeyFetching,
    } = useCoachPayment();

    const creditCards = savedCreditCards || [];

    const newCardCheckoutFormRef = useRef<any>();

    const [addedCards, setAddedCards] = useState<GetUserCreditCardsQuery['userCreditCards']>([]);
    const [phoneError, setPhoneError] = useState<string | null>(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [dialCode, setDialCode] = useState('');

    const defaultCardId = user
      ? creditCards.find((card) => card.id === user.defaultCreditCardId)
      : null;

    const restOfCards = creditCards.filter((card) => card.id !== defaultCardId?.id);
    const sortedCards = [defaultCardId, ...restOfCards];
    const displayCards = [...addedCards, ...sortedCards].filter((card) => !!card);

    const isCardExpired = (card: any) => {
      if (!card) return false;

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      return (
        card.expireYear < currentYear ||
        (card.expireYear === currentYear && card.expireMonth < currentMonth)
      );
    };

    const shouldShowLoadingSkeleton = isStripeKeyFetching || isLoadingCreditCards;

    const handleCompleteSetupIntent = async (setupIntent: SetupIntent) => {
      closeAddCardModal();

      const paymentMehod =
        typeof setupIntent?.payment_method !== 'string' ? setupIntent?.payment_method : null;

      if (paymentMehod) {
        setAddedCards((cards) => [
          {
            __typename: 'UserCreditCards',
            id: '',
            last4: paymentMehod?.card?.last4 || '',
            provider: 'STRIPE',
            providerCardId: paymentMehod.id,
            expireYear: paymentMehod?.card?.exp_year || 0,
            expireMonth: paymentMehod?.card?.exp_month || 0,
            brand: paymentMehod?.card?.brand || '',
            billingName: '',
            billingPostalCode: '',
          },
          ...cards,
        ]);
      }

      setSelectedCardIndex(0);

      try {
        fetchSetupIntent();
      } catch (error) {
        captureException(error);
      } finally {
        //
      }
    };

    const handlePhoneNumberChange = (phone: string, country: any): void => {
      const { dialCode } = country;

      setDialCode(`${toSafePhone(dialCode)}`);

      let inputPhoneNumber: string = toFormattedPhone(phone);
      setPhoneNumber(inputPhoneNumber);
    };

    useImperativeHandle(parentConrolRef, () => ({
      triggerSubmit: () => {
        displayCards.length > 0
          ? formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true })) // local form
          : newCardCheckoutFormRef?.current.triggerSubmit(); // nested form when no card is added
      },
    }));

    return (
      <>
        {!displayCards.length && !shouldShowLoadingSkeleton && (
          <Elements
            stripe={stripeInternal}
            options={{
              clientSecret: stripeKeys?.setupIntentClientSecret,
              loader: 'always',
              appearance: {
                labels: undefined,
              },
            }}
          >
            <NewCardCheckoutForm
              onSubmit={({ paymentMethodId }) => handleSubmitPayment(paymentMethodId)}
              phoneExists={user?.phoneNumber || ''}
              setPhoneError={setPhoneError}
              phoneNumber={phoneNumber}
              handlePhoneNumberChange={handlePhoneNumberChange}
              isRemoteCoaching={false}
              disableSubmit={false}
              phoneError={phoneError}
              setIsStripeReady={setIsStripeReady}
              ref={newCardCheckoutFormRef}
              hideSubmitBtn={hideSubmitBtn}
            />
          </Elements>
        )}
        {displayCards.length > 0 && (
          <form
            ref={formRef}
            onSubmit={async (e) => {
              e.preventDefault();

              if (isLoading) {
                return;
              }

              // Check if selected card is expired
              if (isCardExpired(displayCards[selectedCardIndex])) {
                return;
              }

              await handleSubmitPayment(displayCards[selectedCardIndex].providerCardId);
            }}
            className=""
          >
            <div className="mb-3">
              <button
                className="flex w-full cursor-pointer items-center rounded-lg border border-color-border-card-lightmode p-ds-md text-sm font-medium transition-shadow focus:outline-none dark:border-color-border-card-darkmode"
                type="button"
                onClick={() => openAddCardModal()}
              >
                <Plus className="mr-2 h-[1.375rem] text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
                Add new card
              </button>
            </div>
            <RadioGroup
              value={selectedCardIndex}
              onChange={(index) => {
                // Only allow selection of non-expired cards
                if (!isCardExpired(displayCards[index])) {
                  setSelectedCardIndex(index);
                }
              }}
              disabled={isLoading}
            >
              <RadioGroup.Label className="sr-only">Credit card</RadioGroup.Label>
              <div className="space-y-3 pb-8">
                {displayCards.map((card, index) => {
                  if (!card) {
                    return null;
                  }

                  const expired = isCardExpired(card);

                  return (
                    <RadioGroup.Option
                      key={card.providerCardId}
                      value={index}
                      disabled={expired}
                      className={({ checked }) =>
                        classNames(
                          checked
                            ? 'shadow-[0px_0px_0px_2px_#FFF,0px_0px_0px_3px_#B9B9BE,0px_4px_20px_0px_rgba(0,0,0,0.16)]'
                            : 'border border-color-border-card-lightmode dark:border-color-border-card-darkmode',
                          'flex cursor-pointer flex-col rounded-lg  bg-color-bg-lightmode-primary p-ds-md transition-shadow focus:outline-none dark:bg-color-bg-darkmode-primary',
                          expired ? 'cursor-not-allowed opacity-50' : '',
                        )
                      }
                    >
                      {({ active, checked }) => (
                        <div className="flex w-full items-center justify-between">
                          <div className="flex items-center">
                            <span
                              className={classNames(
                                checked
                                  ? 'border-transparent bg-color-text-lightmode-primary dark:bg-color-text-darkmode-primary'
                                  : 'border-gray-300 bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary',
                                active ? '' : '',
                                'flex h-5 w-5 items-center justify-center rounded-full border',
                              )}
                              aria-hidden="true"
                            >
                              <span className="h-1.5 w-1.5 rounded-full bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary" />
                            </span>
                            <RadioGroup.Label className="ml-3 cursor-pointer text-sm">
                              {CardBrandsDisplayName[card.brand]} <span className="ml-2">••••</span>{' '}
                              {card.last4}
                            </RadioGroup.Label>
                          </div>
                          <div className="flex items-center">
                            {expired && <Pill text="EXPIRED" size="sm" type="critical" />}
                            <RadioGroup.Description as="span" className="ml-2 text-sm">
                              {card.expireMonth}/{card.expireYear}
                            </RadioGroup.Description>
                          </div>
                        </div>
                      )}
                    </RadioGroup.Option>
                  );
                })}
              </div>
            </RadioGroup>

            <div
              className={classNames(
                'sticky bottom-0 w-full bg-color-bg-lightmode-primary  dark:bg-color-bg-darkmode-primary',
                hideSubmitBtn && 'hidden',
              )}
            >
              <Button
                disabled={
                  isLoading ||
                  (displayCards.length > 0 && isCardExpired(displayCards[selectedCardIndex]))
                }
                type="submit"
                variant="primary"
                size="lg"
              >
                {isLoading
                  ? 'Loading...'
                  : displayCards.length > 0 && isCardExpired(displayCards[selectedCardIndex])
                  ? 'Card Expired'
                  : 'Pay'}
              </Button>
            </div>
          </form>
        )}
        <div
          className={classNames(
            displayCards.length === 0 && shouldShowLoadingSkeleton ? 'block' : 'hidden',
            'pb-6',
          )}
        >
          <LoadingSkeletonCollection />
        </div>
        <Modal isOpen={isAddCardModalOpen} handleClose={closeAddCardModal}>
          <div className="p-4">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold leading-7 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                Add credit card
              </h2>
              <button type="button" className="outline-none" onClick={() => closeAddCardModal()}>
                <XMarkIcon className="h-7 w-7 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
              </button>
            </div>
            {!!stripeKeys?.setupIntentClientSecret && (
              <Elements
                stripe={stripeInternal}
                options={{
                  clientSecret: stripeKeys?.setupIntentClientSecret,
                  loader: 'always',
                  appearance: {
                    labels: undefined,
                  },
                }}
              >
                <AddCardForm
                  handleCompleteSetupIntent={handleCompleteSetupIntent}
                  setIsStripeReady={setIsStripeReady}
                />
              </Elements>
            )}
          </div>
        </Modal>
      </>
    );
  },
);

export default ManagePaymentCards;
